import { Avatar, Text, makeStyles, tokens } from '@fluentui/react-components';
import { formatUserDisplayName, isDisplayableValue } from '@/utils/primitives';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
    // Cap how wide the cell can grow so long emails / GUIDs truncate instead
    // of stretching the column and pushing later columns off-screen.
    maxWidth: '200px',
  },
  name: {
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
});

export interface UserCellProps {
  /** Raw identifier (typically a UPN / email). Display + initials are derived from this. */
  name?: string | null | undefined;
  /** Optional explicit initials override. */
  initials?: string;
  /** Fallback text when name is missing. */
  fallback?: string;
}

const computeInitials = (raw: string): string => {
  // Use the part before "@" if it's an email-like string.
  const trimmed = raw.includes('@') ? (raw.split('@')[0] ?? raw) : raw;
  const parts = trimmed.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return raw.slice(0, 2).toUpperCase();
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
};

/**
 * Generic "avatar + display name" cell. Shared by Apps and Workflows tables for
 * the Created by / Owner / Modified by columns. Email-shaped values render
 * with the domain stripped; the full identifier is preserved in the tooltip.
 */
export const UserCell = ({ name, initials, fallback = '—' }: UserCellProps) => {
  const styles = useStyles();

  if (!isDisplayableValue(name)) {
    return <Text className={styles.name}>{fallback}</Text>;
  }

  const computed = initials ?? computeInitials(name);
  const label = formatUserDisplayName(name, name);

  return (
    <div className={styles.root}>
      <Avatar name={name} initials={computed} size={24} color="colorful" aria-hidden="true" />
      <Text className={styles.name} title={name}>
        {label}
      </Text>
    </div>
  );
};
